// Step5AdditionalLicenses.js (final updated)
import React, { useEffect, useState } from "react";
import Select from "react-select";
import axios from "axios";
import { FaTrashAlt } from "react-icons/fa";
import CountryProvinceSelector from "../../../components/CountryProvinceSelector";

const EMPTY_LICENSE = {
  dl_license_no: "",
  dl_class: "",
  dl_condition: "",
  dl_province: "",
  dl_country: "Canada",
  dl_expdate: "",
  dl_driver_status: "inactive",
};

const samePendingLicense = (a = EMPTY_LICENSE, b = EMPTY_LICENSE) =>
  a.dl_license_no === b.dl_license_no &&
  JSON.stringify(a.dl_class) === JSON.stringify(b.dl_class) &&
  JSON.stringify(a.dl_condition) === JSON.stringify(b.dl_condition) &&
  a.dl_province === b.dl_province &&
  a.dl_country === b.dl_country &&
  a.dl_expdate === b.dl_expdate;

const Step5AdditionalLicenses = ({
  countries,
  primaryLicense,
  setForm,
  form,
  prevStep,
  nextStep,
}) => {
  const BASE = process.env.REACT_APP_API_BASE_URL || "";

  const [classOptions, setClassOptions] = useState([]);
  const [conditionOptions, setConditionOptions] = useState([]);
  const [hasOther, setHasOther] = useState(
    form?.additionalLicensesHasOther ||
      ((form?.additionalLicenses || []).length > 0 ? "Yes" : "No"),
  );
  const [licenses, setLicenses] = useState([]);
  const [newLicense, setNewLicense] = useState({
    ...EMPTY_LICENSE,
    ...(form?.pendingAdditionalLicense || {}),
  });

  // Sync local licenses with form state
  useEffect(() => {
    setLicenses(form?.additionalLicenses || []);
  }, [form?.additionalLicenses]);

  useEffect(() => {
    setForm((prev) => {
      if (
        prev.additionalLicensesHasOther === hasOther &&
        samePendingLicense(prev.pendingAdditionalLicense, newLicense)
      ) {
        return prev;
      }

      return {
        ...prev,
        additionalLicensesHasOther: hasOther,
        pendingAdditionalLicense: newLicense,
      };
    });
  }, [hasOther, newLicense, setForm]);

  // Load classes & conditions when province changes
  useEffect(() => {
    if (newLicense.dl_country !== "Canada" || !newLicense.dl_province) {
      setClassOptions([]);
      setConditionOptions([]);
      return;
    }

    const fetchLicenseMeta = async () => {
      try {
        const res = await axios.get(`${BASE}/api/dropdowns/province-by-name`, {
          params: {
            country: newLicense.dl_country,
            province: newLicense.dl_province,
          },
        });

        const provinceId = res.data?.id;
        if (!provinceId) return;

        const [classesRes, conditionsRes] = await Promise.all([
          axios.get(`${BASE}/api/dropdowns/license-classes/${provinceId}`),
          axios.get(`${BASE}/api/dropdowns/license-conditions/${provinceId}`),
        ]);

        setClassOptions(classesRes.data?.classes || []);
        setConditionOptions(conditionsRes.data?.conditions || []);
      } catch (err) {
        console.error("❌ Failed to load license metadata", err);
        setClassOptions([]);
        setConditionOptions([]);
      }
    };

    fetchLicenseMeta();
  }, [newLicense.dl_country, newLicense.dl_province, BASE]);

  // Add new license (local only, save to DB happens in handleComplete)
  const handleAdd = (e) => {
    e.preventDefault();
    if (!newLicense.dl_license_no || !newLicense.dl_province) {
      alert("Please enter License No and Province before adding.");
      return;
    }

    const lic = {
      ...newLicense,
      dl_status: "other",
      dl_driver_status: "inactive",
      dl_license: null,
      dl_license_original: null,
      dl_class:
        typeof newLicense.dl_class === "string"
          ? newLicense.dl_class
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          : newLicense.dl_class,
      dl_condition:
        typeof newLicense.dl_condition === "string"
          ? newLicense.dl_condition
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          : newLicense.dl_condition,
    };

    const updated = [...licenses, lic];
    setLicenses(updated);
    setForm((prev) => ({
      ...prev,
      additionalLicenses: updated,
    }));

    setHasOther("No");
    setNewLicense(EMPTY_LICENSE);
  };

  const handleDelete = (index) => {
    if (!window.confirm("Delete this license?")) return;
    const updated = [...licenses];
    updated.splice(index, 1);
    setLicenses(updated);
    setForm((prev) => ({
      ...prev,
      additionalLicenses: updated,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    nextStep();
  };

  const formatLicenseValue = (value) =>
    Array.isArray(value) ? value.join(", ") : value || "-";

  return (
    <form onSubmit={handleSubmit}>
      <fieldset className="border p-3 mb-4">
        <legend>Additional Licence Information</legend>

        {/* Licence list */}
        {(primaryLicense?.dl_license_no || licenses.length > 0) && (
          <div className="additional-license-list mb-3">
            {primaryLicense?.dl_license_no && (
              <div className="additional-license-card">
                <div className="additional-license-card-main">
                  <div className="additional-license-number">
                    Primary Driver License
                  </div>
                  <div className="additional-license-meta">
                    <span><strong>Driver License:</strong> {primaryLicense.dl_license_no}</span>
                    <span><strong>Class:</strong> {formatLicenseValue(primaryLicense.dl_class)}</span>
                    <span><strong>Condition:</strong> {formatLicenseValue(primaryLicense.dl_condition)}</span>
                    <span><strong>Province:</strong> {primaryLicense.dl_province || "-"}</span>
                    <span><strong>Expiry:</strong> {primaryLicense.dl_expdate || "-"}</span>
                  </div>
                </div>
                <span className="additional-license-badge">Primary</span>
              </div>
            )}
            {licenses.map((lic, index) => (
              <div className="additional-license-card" key={`${lic.dl_license_no}-${index}`}>
                <div className="additional-license-card-main">
                  <div className="additional-license-number">
                    Additional Driver License
                  </div>
                  <div className="additional-license-meta">
                    <span><strong>Driver License:</strong> {lic.dl_license_no}</span>
                    <span><strong>Class:</strong> {formatLicenseValue(lic.dl_class)}</span>
                    <span><strong>Condition:</strong> {formatLicenseValue(lic.dl_condition)}</span>
                    <span><strong>Province:</strong> {lic.dl_province || "-"}</span>
                    <span><strong>Expiry:</strong> {lic.dl_expdate?.slice(0, 10) || "-"}</span>
                  </div>
                </div>
                <button
                  className="additional-license-delete"
                  onClick={() => handleDelete(index)}
                  type="button"
                  aria-label="Delete licence"
                >
                  <FaTrashAlt />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Radio Question */}
        <div className="mb-3">
          <label>
            Have you held any other driver’s licences in the last 3 years?
          </label>
          <div className="btn-group w-100">
            <input
              type="radio"
              className="btn-check"
              name="hasOther"
              id="hasOtherYes"
              value="Yes"
              checked={hasOther === "Yes"}
              onChange={(e) => setHasOther(e.target.value)}
            />
            <label className="btn btn-outline-primary" htmlFor="hasOtherYes">
              Yes
            </label>
            <input
              type="radio"
              className="btn-check"
              name="hasOther"
              id="hasOtherNo"
              value="No"
              checked={hasOther === "No"}
              onChange={(e) => setHasOther(e.target.value)}
            />
            <label className="btn btn-outline-secondary" htmlFor="hasOtherNo">
              No
            </label>
          </div>
        </div>

        {hasOther === "Yes" && (
          <div className="border p-3 mb-3">
            <h6>Add Another License</h6>

            <CountryProvinceSelector
              country={newLicense.dl_country}
              province={newLicense.dl_province}
              countries={countries}
              onChange={(field, value) =>
                setNewLicense((prev) => ({
                  ...prev,
                  [field === "country" ? "dl_country" : "dl_province"]: value,
                }))
              }
            />

            {/* License No */}
            <input
              type="text"
              className="form-control mb-2"
              placeholder="License No"
              value={newLicense.dl_license_no}
              onChange={(e) =>
                setNewLicense((prev) => ({
                  ...prev,
                  dl_license_no: e.target.value,
                }))
              }
            />

            {/* Class */}
            {newLicense.dl_country === "Canada" && classOptions.length > 0 ? (
              <Select
                isMulti
                value={
                  Array.isArray(newLicense.dl_class)
                    ? newLicense.dl_class.map((v) => ({ value: v, label: v }))
                    : []
                }
                options={classOptions.map((c) => ({
                  value: c.class_code,
                  label: c.description
                    ? `${c.class_code} - ${c.description}`
                    : c.class_code,
                }))}
                onChange={(selected) =>
                  setNewLicense((prev) => ({
                    ...prev,
                    dl_class: selected ? selected.map((s) => s.value) : [],
                  }))
                }
                className="mb-2"
              />
            ) : (
              <input
                type="text"
                className="form-control mb-2"
                placeholder="Class (comma separated)"
                value={
                  Array.isArray(newLicense.dl_class)
                    ? newLicense.dl_class.join(",")
                    : newLicense.dl_class
                }
                onChange={(e) =>
                  setNewLicense((prev) => ({
                    ...prev,
                    dl_class: e.target.value,
                  }))
                }
              />
            )}

            {/* Condition */}
            {newLicense.dl_country === "Canada" &&
            conditionOptions.length > 0 ? (
              <Select
                isMulti
                value={
                  Array.isArray(newLicense.dl_condition)
                    ? newLicense.dl_condition.map((v) => ({
                        value: v,
                        label: v,
                      }))
                    : []
                }
                options={conditionOptions.map((c) => ({
                  value: c.condition_code,
                  label: c.description
                    ? `${c.condition_code} - ${c.description}`
                    : c.condition_code,
                }))}
                onChange={(selected) =>
                  setNewLicense((prev) => ({
                    ...prev,
                    dl_condition: selected ? selected.map((s) => s.value) : [],
                  }))
                }
                className="mb-2"
              />
            ) : (
              <input
                type="text"
                className="form-control mb-2"
                placeholder="Condition (comma separated)"
                value={
                  Array.isArray(newLicense.dl_condition)
                    ? newLicense.dl_condition.join(",")
                    : newLicense.dl_condition
                }
                onChange={(e) =>
                  setNewLicense((prev) => ({
                    ...prev,
                    dl_condition: e.target.value,
                  }))
                }
              />
            )}

            {/* Expiry */}
            <input
              type="date"
              className="form-control mb-2"
              value={newLicense.dl_expdate}
              onChange={(e) =>
                setNewLicense((prev) => ({
                  ...prev,
                  dl_expdate: e.target.value,
                }))
              }
            />

            <button
              className="btn btn-success"
              onClick={handleAdd}
              type="button"
            >
              Add License
            </button>
          </div>
        )}

        <div className="d-flex justify-content-between mt-3">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={prevStep}
          >
            ← Back
          </button>
          <button type="submit" className="btn btn-primary">
            Next →
          </button>
        </div>
      </fieldset>
    </form>
  );
};

export default Step5AdditionalLicenses;
